// Хүргэлт удирдлага hub (canvas Ord_DeliveryHub / TabsA / TabsB): 7 sub-tabs
// Хүлээлтэнд / Маршрут / Хүлээлцэл / Идэвхтэй / Амжилтгүй / Хүргэсэн / Бэлэн төлөлт.
// Each tab is gated by the permission its list endpoint checks (BE contract §1), so a
// visible tab never answers 403.

import { Badge, Box, Group, Tabs, Title } from "@mantine/core"
import { PlusIcon } from "@phosphor-icons/react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router"
import useSWR from "swr"
import { DeliveryApi } from "../../apis"
import { ActiveDeliveries } from "../../components/active-deliveries"
import { CustodyTransfers } from "../../components/custody-transfers"
import { DeliveryActionButton } from "../../components/delivery/action-controls"
import { useDeliveryActions } from "../../components/delivery/delivery-actions"
import { DeliveryRoutes } from "../../components/delivery-routes"
import { FailedDeliveries } from "../../components/failed-deliveries"
import { PageLayout } from "../../components/layout"
import { PendingDeliveries } from "../../components/pending-deliveries"
import { useDeliveryStats } from "../../components/pending-deliveries/hooks"
import { IPermissionCode, usePermission } from "../../components/permission"
import { CodReconciliationWorklist } from "../../components/settings/CodReconciliationWorklist"
import { SupplierDeliveredList } from "../../components/supplier-delivered/list"
import { DeniedCard, SegmentedTabs } from "../../components/ui"
import { DisabledReason, HelpButtonGroup, HelpIcon, useHelpContext } from "@dehub/theme"
import { message } from "../../utils/message"

type TabKey = "PENDING" | "ROUTES" | "HANDOVER" | "ACTIVE" | "FAILED" | "CONFIRM" | "COD"

const TAB_ORDER: TabKey[] = ["PENDING", "ROUTES", "HANDOVER", "ACTIVE", "FAILED", "CONFIRM", "COD"]

const TAB_LABEL: Record<TabKey, string> = {
  PENDING: "delivery.tabs.pending",
  ROUTES: "delivery.tabs.routes",
  HANDOVER: "delivery.tabs.handover",
  ACTIVE: "delivery.tabs.active",
  FAILED: "delivery.tabs.failed",
  CONFIRM: "delivery.tabs.delivered",
  COD: "delivery.tabs.cod",
}

/** Tabs that render a section heading (title + «?») above their content; the description lives in the «?» popover. */
const SECTION_KEY: Partial<Record<TabKey, string>> = {
  PENDING: "pending",
  ROUTES: "routes",
  HANDOVER: "handover",
  ACTIVE: "active",
  FAILED: "failed",
  CONFIRM: "delivered",
  COD: "cod",
}

const DEFAULT_TAB: TabKey = "PENDING"

function TabCount({ count, active }: Readonly<{ count: number | undefined; active: boolean }>) {
  if (count == null) return null
  return (
    <Badge size="md" radius="xl" color={active ? "dark" : "gray"} variant={active ? "filled" : "light"} ff="monospace">
      {count}
    </Badge>
  )
}

function TabSection({ title, helpKey }: Readonly<{ title: string; helpKey?: string }>) {
  return (
    <Box mb="md">
      <Title order={3} fz="var(--nw-fz-drawer)">
        {title}
        {helpKey && <HelpIcon helpKey={helpKey} variant="section" />}
      </Title>
    </Box>
  )
}

/** Gate = the permission the tab's list endpoint checks (BE contract §1); several = any of. */
function useTabGate(): Record<TabKey, { ok: boolean; code: IPermissionCode }> {
  const draft = usePermission("ORD_DN_DRAFT_LIST").hasAccess
  const assigned = usePermission("ORD_DN_ASSIGNED_LIST").hasAccess
  const delivered = usePermission("ORD_DN_DELIVERED_LIST").hasAccess
  const mng = usePermission("ORD_DN_MNG").hasAccess
  const plan = usePermission("DEL_ROUTE_PLAN").hasAccess
  return {
    PENDING: { ok: draft, code: "ORD_DN_DRAFT_LIST" },
    // delivery-route/list.ts checks ORD_DN_MNG | DEL_ROUTE_PLAN.
    ROUTES: { ok: mng || plan, code: "DEL_ROUTE_PLAN" },
    HANDOVER: { ok: assigned, code: "ORD_DN_ASSIGNED_LIST" },
    ACTIVE: { ok: assigned, code: "ORD_DN_ASSIGNED_LIST" },
    // failed-global-list.ts and cod-reconciliation/list.ts check ORD_DN_MNG (O102, O113).
    FAILED: { ok: mng, code: "ORD_DN_MNG" },
    CONFIRM: { ok: delivered, code: "ORD_DN_DELIVERED_LIST" },
    COD: { ok: mng, code: "ORD_DN_MNG" },
  }
}

export function SupplierDeliveryTabs() {
  const { t } = useTranslation(["delivery", "common"])
  const navigate = useNavigate()
  const location = useLocation()
  const { permissionState } = useDeliveryActions()
  const gate = useTabGate()

  const tabFromUrl = (): TabKey => {
    const value = new URLSearchParams(location.search).get("tab") as TabKey | null
    return value && TAB_ORDER.includes(value) ? value : DEFAULT_TAB
  }

  const [activeTab, setActiveTab] = useState<TabKey>(tabFromUrl())
  const deliveredListRef = useRef<{ reload?: () => void } | null>(null)
  const { stats } = useDeliveryStats()
  // O108: the Идэвхтэй badge counts the same 4 statuses its list shows (incl. PENDING_HANDOVER).
  const { data: activeStats } = useSWR(gate.ACTIVE.ok ? ["swr.delivery.active-stats", null] : null, () =>
    DeliveryApi.activeStats({}),
  )

  useEffect(() => {
    const next = tabFromUrl()
    if (next !== activeTab) setActiveTab(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  // O99: the approve page returns here with ?success=true after the last warehouse is confirmed.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get("success") === "true") {
      message.success(t("manage.hub.approveSuccess"))
      params.delete("success")
      navigate(`/supplier-delivery?${params.toString()}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const onTabChange = (value: string | null) => {
    const next = (value as TabKey) ?? DEFAULT_TAB
    setActiveTab(next)
    const params = new URLSearchParams(location.search)
    params.set("tab", next)
    navigate(`/supplier-delivery?${params.toString()}`, { replace: true })
    if (next === "CONFIRM") {
      setTimeout(() => deliveredListRef.current?.reload?.(), 200)
    }
  }

  const counts: Record<TabKey, number | undefined> = {
    PENDING: stats?.tabs.pending,
    ROUTES: stats?.tabs.routes,
    HANDOVER: stats?.tabs.handover,
    ACTIVE: activeStats?.active ?? stats?.tabs.active,
    FAILED: stats?.tabs.failed,
    CONFIRM: stats?.tabs.delivered,
    COD: stats?.tabs.cod,
  }

  // @dehub/help: the tab is the page content variant (`when`); the tab counts feed the «Одоо» cards.
  const { set: setHelpContext } = useHelpContext()
  useEffect(() => {
    setHelpContext({
      when: activeTab,
      vars: { pendingCount: counts.PENDING, routesCount: counts.ROUTES, handoverCount: counts.HANDOVER, activeCount: counts.ACTIVE, failedCount: counts.FAILED, deliveredCount: counts.CONFIRM, codCount: counts.COD },
    })
  }, [activeTab, counts.PENDING, counts.ROUTES, counts.HANDOVER, counts.ACTIVE, counts.FAILED, counts.CONFIRM, counts.COD, setHelpContext])

  const panel = (tab: TabKey) => {
    switch (tab) {
      case "PENDING":
        return <PendingDeliveries />
      case "ROUTES":
        return <DeliveryRoutes />
      case "HANDOVER":
        return <CustodyTransfers showHeader={false} />
      case "ACTIVE":
        return <ActiveDeliveries />
      case "FAILED":
        return <FailedDeliveries />
      case "CONFIRM":
        return <SupplierDeliveredList ref={deliveredListRef} />
      case "COD":
        return <CodReconciliationWorklist />
    }
  }

  return (
    <PageLayout
      title={t("delivery.title")}
      subTitle={t("delivery.subtitle")}
      breadcrumb={[{ to: "/", label: t("common:breadcrumb.home") }, { label: t("delivery.breadcrumb") }]}
      extra={[
        // Primary entry point: «?» (R2) + reason popover without the permission (R4, @dehub/help).
        <DisabledReason key="new-route" helpKey="order.supplier-delivery-list.reason.new-route" reason="PERM" when={!permissionState("createRoute").allowed} position="bottom-end">
          <HelpButtonGroup helpKey="order.supplier-delivery-list.action.new-route">
            <DeliveryActionButton
              data-help-id="new-route"
              state={permissionState("createRoute")}
              variant="default"
              leftSection={<PlusIcon size={16} />}
              onClick={() => navigate("/delivery-routes/new")}
            >
              {t("manage.hub.newRoute")}
            </DeliveryActionButton>
          </HelpButtonGroup>
        </DisabledReason>,
      ]}
    >
      {/* Segmented tabs with counts: one list-header concept with the other lists and de-network-admin. */}
      {/* The page layout's own 20px gap separates the tabs from the section heading; no extra margins. */}
      <Group>
        <SegmentedTabs
          value={activeTab}
          onChange={onTabChange}
          data={TAB_ORDER.map((tab) => ({
            value: tab,
            label: (
              <Group gap={6} wrap="nowrap">
                {t(TAB_LABEL[tab])}
                <TabCount count={counts[tab]} active={tab === activeTab} />
              </Group>
            ),
          }))}
        />
      </Group>
      <Tabs value={activeTab} keepMounted={false}>

        {TAB_ORDER.map((tab) => {
          const section = SECTION_KEY[tab]
          return (
            <Tabs.Panel key={tab} value={tab}>
              {gate[tab].ok ? (
                <>
                  {section && (
                    <TabSection
                      title={t(`manage.section.${section}.title`)}
                      helpKey={`order.supplier-delivery-list.section.${section}`}
                    />
                  )}
                  {panel(tab)}
                </>
              ) : (
                <DeniedCard permission={gate[tab].code} title={t("manage.hub.deniedTitle")} />
              )}
            </Tabs.Panel>
          )
        })}
      </Tabs>
    </PageLayout>
  )
}
